import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { Target, Users, Award, TrendingUp } from 'lucide-react';

const About = () => {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const stats = [
    { icon: Target, value: '125,000+', label: 'Créditos de Carbono Verificados', suffix: '' },
    { icon: Users, value: '200+', label: 'Empleos Generados', suffix: '' },
    { icon: Award, value: '15+', label: 'Años de Experiencia', suffix: '' },
    { icon: TrendingUp, value: '890K', label: 'Ton CO₂ Secuestradas', suffix: '' },
  ];

  return (
    <section id="nosotros" style={{
      background: 'var(--gradient-dark)',
      padding: 'var(--space-3xl) 0',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background decoration */}
      <div style={{
        position: 'absolute',
        top: '-20%',
        right: '-10%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(16,185,129,0.05) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      <div className="container" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 80px' }}
        >
          <span style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '50px',
            color: 'var(--primary-400)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px',
          }}>
            Quiénes Somos
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '20px',
          }}>
            Compromiso con el{' '}
            <span className="text-gradient">Planeta</span>
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
          }}>
            Combinamos la innovación tecnológica brasileña con la riqueza natural de Paraguay 
            para ofrecer soluciones de carbono de clase mundial.
          </p>
        </motion.div>

        {/* Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center',
        }} className="about-grid">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ position: 'relative' }}
          >
            <div style={{
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-xl)',
            }}>
              <img
                src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Bosque Paraguayo"
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                }}
              />
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(5,46,22,0.6) 0%, transparent 50%)',
              }} />
            </div>

            {/* Floating card */}
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                position: 'absolute',
                bottom: '-30px',
                right: '-30px',
                background: 'var(--dark-800)',
                border: '1px solid var(--border)',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '260px',
                boxShadow: 'var(--shadow-xl)',
              }}
              className="about-float-card"
            >
              <div style={{
                width: '48px',
                height: '48px',
                background: 'var(--gradient-primary)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
              }}>
                <Award size={24} color="white" />
              </div>
              <h4 style={{ fontSize: '1.1rem', marginBottom: '8px' }}>Certificación VCS</h4>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Verificado por el Verified Carbon Standard
              </p>
            </motion.div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '2.2rem',
              marginBottom: '24px',
            }}>
              Tradición e <span className="text-gradient-accent">Innovación</span>
            </h3>

            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.8,
              marginBottom: '20px',
              fontSize: '1.05rem',
            }}>
              Con raíces en la tradición de sostenibilidad brasileña, hemos establecido nuestras 
              operaciones en <strong style={{ color: 'var(--primary-400)' }}>Naranjal, Alto Paraná</strong>, 
              aprovechando la extraordinaria biodiversidad de la región y su posición estratégica 
              para el mercado sudamericano de carbono.
            </p>

            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.8,
              marginBottom: '30px',
              fontSize: '1.05rem',
            }}>
              Nuestra plataforma tecnológica de última generación permite el monitoreo satelital 
              en tiempo real de proyectos de captura de carbono, garantizando transparencia total 
              y cumplimiento de los más altos estándares internacionales.
            </p>

            {/* Features list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '40px' }}>
              {[
                'Monitoreo satelital 24/7 de proyectos forestales',
                'Blockchain para trazabilidad de créditos',
                'Certificación internacional VCS y Gold Standard',
                'Equipo multidisciplinario brasileño-paraguayo',
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      background: 'var(--primary-500)',
                      borderRadius: '50%',
                    }} />
                  </div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>{item}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '24px',
              paddingTop: '30px',
              borderTop: '1px solid var(--border)',
            }}>
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px',
                  }}>
                    <stat.icon size={20} style={{ color: 'var(--primary-400)' }} />
                    <span style={{
                      fontSize: '1.8rem',
                      fontWeight: 800,
                      background: 'var(--gradient-primary)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}>
                      {stat.value}
                    </span>
                  </div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .about-grid {
            grid-template-columns: 1fr !important;
          }
          .about-float-card {
            right: 10px !important;
            bottom: -20px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default About;
